"use client";

import { cn } from "@/lib/utils";
import localizePrice from "@/utils/pricing/localizePrice";
import type { RangeFacet } from "@apptus/esales-api";
import { Market } from "@features/market/config/i18n-config";
import { useRouter, useSearchParams } from "next/navigation";
import type { ComponentProps } from "react";
import { toFilterParam } from "../const";
import removeUnwantedQueryParams from "../services/removeUnwantedQueryParams";
import ExponentialRangeFilter from "./ExponentialRangeSlider";
import RangeSlider from "./RangeSlider";

export default function RangeFilter({
  facet,
  market,
  className,
  ...props
}: { facet: RangeFacet; market: Market } & ComponentProps<"section">) {
  const router = useRouter();
  const searchParams = useSearchParams();

  const value = [
    facet.minSelected ?? facet.min ?? 0,
    facet.maxSelected ?? facet.max ?? 1,
  ];

  function onChange(values: number[]) {
    const facetName = toFilterParam("f." + facet.id);
    const [min = 0, max = 1] = values;
    const params = new URLSearchParams(searchParams);

    if (min === facet.min && max === facet.max) {
      params.delete(facetName);
    } else {
      params.set(facetName, `${Math.floor(min)}-${Math.floor(max)}`);
    }

    router.replace(`?${removeUnwantedQueryParams(params).toString()}`);
  }

  if (facet.id === "price") {
    return (
      <section className={cn("pb-4", className)} {...props}>
        <ExponentialRangeFilter
          min={facet.min}
          max={facet.max}
          value={value}
          onChange={onChange}
          formatter={(value) => localizePrice(value, market.locale)}
        />
      </section>
    );
  }
  return (
    <section className={cn("pb-4", className)} {...props}>
      <RangeSlider
        min={facet.min}
        max={facet.max}
        value={value}
        onChange={onChange}
        formatter={(value) => `${value} ${facet.unit}`}
      />
    </section>
  );
}
